import type { ComponentProps } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";

import { DropdownMenuPortal } from "./DropdownMenuPortal";
import { DropdownMenuTrigger } from "./DropdownMenuTrigger";
import { DropdownMenuContent } from "./DropdownMenuContent";
import { DropdownMenuGroup } from "./DropdownMenuGroup";
import { DropdownMenuLabel } from "./DropdownMenuLabel";
import { DropdownMenuItem } from "./DropdownMenuItem";
import { DropdownMenuCheckboxItem } from "./DropdownMenuCheckboxItem";
import { DropdownMenuRadioGroup } from "./DropdownMenuRadioGroup";
import { DropdownMenuRadioItem } from "./DropdownMenuRadioItem";
import { DropdownMenuSeparator } from "./DropdownMenuSeparator";
import { DropdownMenuShortcut } from "./DropdownMenuShortcut";
import { DropdownMenuSub } from "./DropdownMenuSub";
import { DropdownMenuSubTrigger } from "./DropdownMenuSubTrigger";
import { DropdownMenuSubContent } from "./DropdownMenuSubContent";

// Namespaced compound (DropdownMenu.Trigger / .Content / .Item / …), matching Radix's own
// Root/Trigger/Content shape -- a menu is a system of consumer-arranged parts (trigger, items,
// groups, separators, optional submenus), the same class of component as Tooltip/Select. Root
// owns no state of its own beyond what Radix's own Root provides.
export type DropdownMenuProps = ComponentProps<typeof DropdownMenuPrimitive.Root>;

// `modal` is passed straight through -- Radix's own default (`true`) is what gives a dropdown menu
// its focus trap, outside-pointer blocking, scroll lock, and hideOthers screen-reader isolation.
// Do not default it to `false` here: that's a real behavior change for every consumer that doesn't
// pass `modal` explicitly, not a style tweak. (A modal menu's trigger legitimately gets
// `aria-hidden` while focused per Radix's own hideOthers -- axe flags that as aria-hidden-focus;
// silence it per-story with `modal={false}` on the specific story under test, not here.)
const DropdownMenuRoot = (props: DropdownMenuProps) => (
  <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
);

export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Portal: DropdownMenuPortal,
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
  Group: DropdownMenuGroup,
  Label: DropdownMenuLabel,
  Item: DropdownMenuItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioGroup: DropdownMenuRadioGroup,
  RadioItem: DropdownMenuRadioItem,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
});
